import type { OnlineEnemy, OnlinePlayer, RoomSnapshot } from './online';

export const ONLINE_WORLD_MIN_X = 70;
export const ONLINE_WORLD_MAX_X = 2530;
export const ONLINE_VIEWPORT_PADDING_X = 70;

export type OnlineWorldView = {
  localPlayer: OnlinePlayer | null;
  remotePlayers: OnlinePlayer[];
  activeEnemy: OnlineEnemy | null;
};

export function projectOnlineWorld(
  snapshot: RoomSnapshot,
  playerId: string | null,
  currentEnemyId: string | null,
): OnlineWorldView {
  const localPlayer = snapshot.players.find((player) => player.id === playerId) ?? null;
  const remotePlayers = snapshot.players
    .filter((player) => player.id !== playerId)
    .sort((left, right) => left.id.localeCompare(right.id));

  const boss = snapshot.enemies.find((enemy) => enemy.boss && enemy.hp > 0);
  const current = snapshot.enemies.find((enemy) => enemy.id === currentEnemyId && enemy.hp > 0);
  const firstLive = snapshot.enemies.find((enemy) => enemy.hp > 0);

  return {
    localPlayer,
    remotePlayers,
    activeEnemy: boss ?? current ?? firstLive ?? null,
  };
}

export function worldToViewportX(worldX: number, viewportWidth: number): number {
  const left = Math.min(ONLINE_VIEWPORT_PADDING_X, Math.max(0, viewportWidth / 2));
  const right = Math.max(left, viewportWidth - left);
  const ratio = (PhaserClamp(worldX, ONLINE_WORLD_MIN_X, ONLINE_WORLD_MAX_X) - ONLINE_WORLD_MIN_X)
    / (ONLINE_WORLD_MAX_X - ONLINE_WORLD_MIN_X);
  return left + ratio * (right - left);
}

export function viewportToWorldX(viewportX: number, viewportWidth: number): number {
  const left = Math.min(ONLINE_VIEWPORT_PADDING_X, Math.max(0, viewportWidth / 2));
  const right = Math.max(left, viewportWidth - left);
  if (right === left) return ONLINE_WORLD_MIN_X;
  const ratio = (PhaserClamp(viewportX, left, right) - left) / (right - left);
  return ONLINE_WORLD_MIN_X + ratio * (ONLINE_WORLD_MAX_X - ONLINE_WORLD_MIN_X);
}

function PhaserClamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}
